import type { PopoverProps } from '@mantine/core';
import { createStyles, Stack } from '@mantine/core';
import { Text } from '@repo/foundations';
import React from 'react';
import { Link } from 'react-router';
import { HoverCard } from '../HoverCard';

interface RichTooltipProps {
	title?: string;
	linkLabel?: string;
	linkUrl?: string;
	hidden?: boolean;
	body: React.ReactNode;
	children: React.ReactNode;
	maxWidth?: number;
	position?: PopoverProps['position'];
	targetFullWidth?: boolean;
}

const useStyles = createStyles((theme, maxWidth?: number) => ({
	dropdown: {
		borderRadius: theme.other.borderRadius.md,
		boxShadow: theme.shadows.md,
		padding: theme.spacing.sm,
		gap: theme.spacing.sm,
		backgroundColor: theme.other.getColor('surface/primary/default'),
		maxWidth: maxWidth ? maxWidth : theme.other.space[90],
		whiteSpace: 'pre-wrap',
	},
	linkText: {
		textDecoration: 'none',
		color: theme.other.getColor('text/emphasis/default'),
		display: 'flex',
		alignItems: 'center',
	},
	textBox: {
		gap: theme.spacing['3xs'],
		flexWrap: 'wrap',
	},
}));

function RichTooltip({
	title,
	linkLabel,
	linkUrl,
	body,
	children,
	position,
	hidden,
	maxWidth,
	targetFullWidth,
	...other
}: RichTooltipProps & Partial<PopoverProps>) {
	const { classes, theme } = useStyles(maxWidth);

	return (
		<HoverCard
			withinPortal={true}
			position={position}
			target={children}
			hidden={hidden}
			targetFullWidth={targetFullWidth}
			openDelay={1000}
			{...other}
		>
			<Stack className={classes.dropdown}>
				<Stack spacing={theme.other.space[1]} className={classes.textBox}>
					{title && (
						<Text weight="bold" size="sm">
							{title}
						</Text>
					)}
					<Text color="text/secondary/default" size="sm" w="100%">
						{body}
					</Text>
				</Stack>
				{linkLabel && linkUrl && (
					<Text weight="semibold" size="sm">
						<Link className={classes.linkText} to={linkUrl}>
							{linkLabel}
						</Link>
					</Text>
				)}
			</Stack>
		</HoverCard>
	);
}

export default RichTooltip;
